import {
  IMAGE_MIME_TYPE_PREFIX,
  MMS_MAX_IMAGE_SIZE,
  RCS_CARD_MAX_MEDIA_SIZE,
  RCS_CAROUSEL_CARD_MAX_MEDIA_SIZE,
  RCS_CAROUSEL_TOTAL_MAX_SIZE,
  VIDEO_MIME_TYPE_PREFIX,
} from 'components/flowBuilder/constants/files';
import { RcsMessageCategories } from 'constants/rcs';
import { RcsMessageCategory } from 'types/rcs';

export interface ValidateImageFileOptions {
  maxImageSize?: number;
  maxImageSizeErrorMessage?: string;
  rcsMessageCategory?: RcsMessageCategory;
  skipSizeValidation?: boolean;
}

export const validateImageFile = (
  file: File,
  options: ValidateImageFileOptions = {},
) => {
  if (!file.type.startsWith(IMAGE_MIME_TYPE_PREFIX))
    throw new Error('File type is not allowed');

  const {
    maxImageSize,
    maxImageSizeErrorMessage,
    rcsMessageCategory,
    skipSizeValidation,
  } = options;

  if (skipSizeValidation) return;

  const defaultMaxImageSize =
    rcsMessageCategory === RcsMessageCategories.CAROUSEL
      ? RCS_CAROUSEL_CARD_MAX_MEDIA_SIZE
      : rcsMessageCategory === RcsMessageCategories.CARD
      ? RCS_CARD_MAX_MEDIA_SIZE
      : MMS_MAX_IMAGE_SIZE;

  const finalMaxImageSize = maxImageSize ?? defaultMaxImageSize.value;

  const finalMaxImageSizeErrorMessage =
    maxImageSizeErrorMessage ?? defaultMaxImageSize.errorMessage;

  if (file.size > finalMaxImageSize)
    throw new Error(finalMaxImageSizeErrorMessage);
};

export interface ValidateVideoFileOptions {
  maxVideoSize?: number;
  maxVideoSizeErrorMessage?: string;
  rcsMessageCategory?: RcsMessageCategory;
  skipSizeValidation?: boolean;
}

export const validateVideoFile = (
  file: File,
  options: ValidateVideoFileOptions = {},
) => {
  if (!file.type.startsWith(VIDEO_MIME_TYPE_PREFIX))
    throw new Error('File type is not allowed');

  const {
    maxVideoSize,
    maxVideoSizeErrorMessage,
    rcsMessageCategory,
    skipSizeValidation,
  } = options;

  if (skipSizeValidation) return;

  const defaultMaxVideoSize =
    rcsMessageCategory === RcsMessageCategories.CAROUSEL
      ? RCS_CAROUSEL_CARD_MAX_MEDIA_SIZE // 10MB per carousel card
      : RCS_CARD_MAX_MEDIA_SIZE; // 100MB for single RCS card

  const finalMaxVideoSize = maxVideoSize ?? defaultMaxVideoSize.value;

  const finalMaxVideoSizeErrorMessage =
    maxVideoSizeErrorMessage ?? defaultMaxVideoSize.errorMessage;

  if (file.size > finalMaxVideoSize)
    throw new Error(finalMaxVideoSizeErrorMessage);
};

/**
 * Validates that the total combined size of all videos in a carousel
 * does not exceed 100MB as per Google RBM requirements.
 */
export const validateCarouselTotalVideoSize = (
  videoFiles: File[],
  maxTotalSize: number = RCS_CAROUSEL_TOTAL_MAX_SIZE.value,
  errorMessage: string = RCS_CAROUSEL_TOTAL_MAX_SIZE.errorMessage,
) => {
  const totalSize = videoFiles.reduce((sum, file) => sum + file.size, 0);

  if (totalSize > maxTotalSize) {
    throw new Error(errorMessage);
  }
};

/**
 * Derives the MIME type from a video URL's file extension.
 * Supports formats: mp4, m4v, webm, mpeg, mpeg4, h263
 */
export const getVideoMimeType = (url: string): string | undefined => {
  // Strip query string and fragment before extracting extension
  const cleanUrl = url.split(/[?#]/)[0];
  const extension = cleanUrl.split('.').pop()?.toLowerCase();
  const mimeTypeMap: Record<string, string> = {
    mp4: 'video/mp4',
    m4v: 'video/m4v',
    webm: 'video/webm',
    mpeg: 'video/mpeg',
    mpeg4: 'video/mpeg4',
    h263: 'video/h263',
  };
  return extension ? mimeTypeMap[extension] : undefined;
};
